// File: src/api/feedback.ts
// Purpose: API types and methods for feedback functionality

import { ChatAppResponse } from "./models";
import { getBackendUri } from "./api";
import type { ClosureReason } from "../utils/answerClosure";
import type { RatingLabel } from "../utils/ratingLabels";

export interface ConversationMessage {
    role: "user" | "assistant";
    content: string;
    timestamp?: string;
}

export interface FeedbackMetadata {
    model_used?: string;
    approach?: string;
    intent?: string;
    search_category?: string;
    intent_model?: string;
    response_model?: string;
    reasoning_effort?: string;
}

// ENG-027: what showed the rating prompt — the idle timer after an answer that closes, or
// the host page moving to another product.
export type RatingPromptTrigger = "closure_idle" | "host_context_change";

export interface FeedbackClosure {
    closes: boolean;
    reason: ClosureReason;
    source: "gateway" | "frontend";
}

// ENG-027 optional fields of the three-choice prompt (rating stays 1..5: Bad 1, Fine 3, Good 5).
export interface RatingPromptFeedback {
    rating_scale?: 3 | 5;
    rating_label?: RatingLabel;
    reason_tags?: string[]; // <= 5, each <= 40 characters
    prompt_trigger?: RatingPromptTrigger;
    answer_index?: number;
    closure?: FeedbackClosure;
}

export interface FeedbackSubmission extends RatingPromptFeedback {
    feedback_id?: string;
    rating: number; // 1-5
    comment?: string;
    session_id?: string;
    turn_id?: string;
    conversation_history: ConversationMessage[];
    metadata?: FeedbackMetadata;

    user_id?: string;
    user_name?: string;
    user_email?: string;
}

export interface FeedbackResponse {
    success: boolean;
    feedback_id: string;
    message: string;
    updated?: boolean;
}

// API Methods
export async function submitFeedback(feedback: FeedbackSubmission, token?: string): Promise<FeedbackResponse> {
    const headers: HeadersInit = {
        "Content-Type": "application/json"
    };

    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }
    const baseUrl = getBackendUri();
    const response = await fetch(`${baseUrl}/feedback`, {
        method: "POST",
        headers,
        body: JSON.stringify(feedback)
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to submit feedback");
    }

    return await response.json();
}

// Helper function to prepare feedback data from chat context
export function prepareFeedbackData(
    rating: number,
    comment: string,
    answers: [string, ChatAppResponse][],
    sessionId?: string,
    user?: { name?: string; email?: string; id?: string },
    modelSettings?: { intentModel?: string; responseModel?: string; reasoningEffort?: string },
    feedbackId?: string,
    promptFields?: RatingPromptFeedback
): FeedbackSubmission {
    // Build full conversation history
    const conversationHistory: ConversationMessage[] = [];
    const latestSessionState = answers.length ? answers[answers.length - 1][1].choices[0].session_state : undefined;

    for (const [question, response] of answers) {
        conversationHistory.push({
            role: "user",
            content: question,
            timestamp: new Date().toISOString()
        });

        conversationHistory.push({
            role: "assistant",
            content: response.choices[0].message.content,
            timestamp: new Date().toISOString()
        });
    }

    const metadata: FeedbackMetadata = {
        model_used: modelSettings?.responseModel || "gpt-4o",
        approach: "chat",
        intent_model: modelSettings?.intentModel,
        response_model: modelSettings?.responseModel,
        reasoning_effort: modelSettings?.reasoningEffort
    };

    return {
        feedback_id: feedbackId,
        rating,
        comment,
        session_id: latestSessionState?.n8n_session_id || sessionId || generateSessionId(),
        turn_id: answers.length ? answers[answers.length - 1][1].choices[0].session_state?.turn_id : undefined,
        conversation_history: conversationHistory,
        metadata,
        user_id: user?.id,
        user_name: user?.name || user?.id,
        user_email: user?.email,
        ...promptFields
    };
}

// Generate a simple session ID if none exists
function generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
